// Global Instructions Rule Applied!
//
// Fundraising History — every fundraising process this investor has been in,
// whatever came of it (LP agent programme Phase 10, owner 2026-09-05).
//
// WHY it is here and not on the Dashboard. Phase 10 stops out the prospects of
// a fund that has finished raising: they keep their pipeline state but drop
// off the Dashboard's Fundraising card, out of the ingest rule's open set and
// out of the agents' context. The Dashboard is action items (the Fundraising
// card is whose-move, and hides itself when nothing is open); this is standing
// knowledge, and must show for an LP with nothing open at all — which is
// exactly the LP whose history matters most when the next fund opens.
//
// Standard kit: FormTable (which renders its own FormSection) with no actions
// column, per docs/FRONTEND_DESIGN_PATTERNS.md. Read-only — every value on the
// row is derived from the prospect, the fund and the commitments, so there is
// nothing here to edit that isn't edited somewhere better. The one action is
// "+ Prospect" in the header (owner, 2026-10-02): the New Prospect drawer with
// this account filled in — the same button as "+ Deal" on Deals & Rounds.
//
// OUTCOME (owner, 2026-09-05). Four values, and the distinction that earns the
// column is Declined vs Not invested: an investor who told us no is a
// different fact from one the fundraise simply closed around, and only the
// first is a decision we were given. The RPC tests them in that order.
//
// The outcome's label and colour come from cfg.pipeline_outcomes, computed in
// view__lp_fundraising_history — there is no vocabulary in this file at all.
// Rename "Not invested" in Configuration and this table follows with no
// deploy. The ⚑ that stood here — 'Declined' covering the whole terminal set,
// overstating 11 of 22 rows — is closed: `Unlikely` is no longer terminal, so
// a cold process reads Open while the fund raises.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Tag, Tooltip } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { FormTable, FormTableRecord } from '../../../core/components/FormTable/index.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { fetchLpFundraisingHistory } from '../services/internalAgentsService.ts'
import { LpFundraisingHistoryRow } from '../../../state_manager/data_models/internal_agents_model.ts'
import { prospectFormPath } from '../../relationships/utils/openRecord.ts'
import { useQuickAdd } from '../../../core/contexts/QuickAddContext.jsx'
import { ProspectFields } from '../../fundraising/utils/fieldConfigs.ts'

interface LpFundraisingHistorySectionProps {
  accountId: string | null | undefined
  /** Label of the investor the "+ Prospect" drawer opens with. */
  accountName?: string | null
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
  /** Render nothing for an LP we have never raised from. Off by default: on
   *  the Relationship tab an empty section is honest ("we have never asked
   *  them"), and the tab is not action items. */
  hideWhenEmpty?: boolean
}

type Row = LpFundraisingHistoryRow & FormTableRecord

export const LpFundraisingHistorySection: React.FC<LpFundraisingHistorySectionProps> = ({
  accountId,
  accountName = null,
  onRefreshReady = null,
  hideWhenEmpty = false
}) => {
  const [rows, setRows] = useState<LpFundraisingHistoryRow[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const quickAdd = useQuickAdd()
  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    if (!accountId) {
      setRows([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      setRows(await fetchLpFundraisingHistory(accountId, token()))
    } catch (error) {
      console.error('[internal-agents] LP fundraising history load failed:', error)
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [accountId])

  useEffect(() => {
    load()
  }, [load])

  // The app shell hides tabs with display:none rather than unmounting them, so
  // a mount-time-only fetch goes stale. Same contract as the sibling sections.
  useEffect(() => {
    if (onRefreshReady) onRefreshReady(load)
  }, [onRefreshReady, load])

  // "+ Prospect" saves in a drawer OVER this tab, so nothing re-activates the
  // section. The save does add the prospect to the fundraising slice — watch
  // this account's count there and reload when it moves (the Deals section of
  // AccountDealsRoundsTab does the same with the deals slice).
  const accountProspectCount = useSelector(
    (state: any) =>
      ((state.fundraising?.data || []) as Array<Record<string, unknown>>).filter(
        (p) => p[ProspectFields.prospects__investor] === accountId
      ).length
  )
  const prospectCountSeenRef = useRef<number | null>(null)
  useEffect(() => {
    if (prospectCountSeenRef.current !== null && prospectCountSeenRef.current !== accountProspectCount) {
      void load()
    }
    prospectCountSeenRef.current = accountProspectCount
  }, [accountProspectCount, load])

  const dataSource: Row[] = useMemo(
    () => rows.map((r) => ({ ...r, key: r.prospects__id })),
    [rows]
  )

  const columns: ColumnsType<Row> = [
    {
      title: 'Fund',
      dataIndex: 'funds__name',
      key: 'fund',
      width: 190,
      render: (v: string | null, r) => (
        <button
          type='button'
          className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
          onClick={() => navigate(prospectFormPath(r.prospects__id))}
        >
          {v || r.prospects__name || 'Fundraising process'}
        </button>
      )
    },
    {
      title: 'Outcome',
      dataIndex: 'outcome',
      key: 'outcome',
      width: 120,
      render: (_v: string, r) => {
        // The decision status rides on the tooltip: it is the stage-level
        // detail behind the outcome, and it has nowhere else to appear now
        // that the Stage column is the stage alone.
        const status = r.pipeline_statuses__name
        const tag = (
          <Tag color={r.outcome_tag_color ?? 'default'} style={{ marginInlineEnd: 0 }}>
            {r.outcome_label ?? r.outcome}
          </Tag>
        )
        return status ? <Tooltip title={`Decision status: ${status}.`}>{tag}</Tooltip> : tag
      }
    },
    {
      title: 'Stage',
      dataIndex: 'pipeline_stages__name',
      key: 'stage',
      width: 160,
      render: (v: string | null) => v || <span className='text-gray-400'>Not in a pipeline</span>
    },
    {
      title: 'Stage date',
      dataIndex: 'pipeline_prospects__stage_date',
      key: 'stage_date',
      width: 100,
      render: (v: string | null) => (v ? dayjs(v).format('MM/DD/YYYY') : <span className='text-gray-400'>—</span>)
    },
    {
      title: 'Commitment',
      key: 'commitment',
      width: 120,
      align: 'right',
      // The money that came of it if they invested, otherwise what we asked
      // for — two different numbers, so the row says which it is showing.
      render: (_: unknown, r) =>
        r.outcome === 'invested' && r.fund_commitments__amount != null ? (
          <span className='tabular-nums font-semibold'>{r.fund_commitments__amount_fmt}</span>
        ) : r.prospects__commitment != null ? (
          <Tooltip title='Target commitment on the prospect — not money received.'>
            <span className='tabular-nums text-gray-500'>{r.prospects__commitment_fmt}</span>
          </Tooltip>
        ) : (
          <span className='text-gray-400'>—</span>
        )
    },
    {
      title: 'Fund status',
      dataIndex: 'fund_statuses__name',
      key: 'fund_status',
      width: 110,
      render: (v: string | null, r) =>
        v ? <Tag color={r.fund_statuses__is_open ? 'blue' : 'default'}>{v}</Tag> : <span className='text-gray-400'>—</span>
    },
    {
      // When the fund's status was RECORDED, from the audit trail — nothing
      // stores it on the fund itself. "Since" rather than "date" because that
      // is what it answers: how long the fund has been in this state.
      title: 'Status since',
      dataIndex: 'funds__fundraising_status_since',
      key: 'status_since',
      width: 110,
      render: (v: string | null) => (
        v ? <span className='tabular-nums'>{dayjs(v).format('MM/DD/YYYY')}</span>
          : <Tooltip title='This fund’s fundraising status has never been changed in Reservoir, so there is no date for it.'>
              <span className='text-gray-400'>—</span>
            </Tooltip>
      )
    },
    {
      title: 'Interactions',
      dataIndex: 'interactions_count',
      key: 'interactions',
      width: 90,
      align: 'right',
      render: (n: number) => <span className='tabular-nums'>{n || 0}</span>
    },
    {
      title: 'Last interaction',
      key: 'last_interaction',
      width: 140,
      render: (_: unknown, r) =>
        r.last_interaction_at ? (
          <span>
            {dayjs(r.last_interaction_at).format('MM/DD/YYYY')}
            {r.last_interaction_type ? (
              <span className='text-gray-400 ml-1'>{r.last_interaction_type}</span>
            ) : null}
          </span>
        ) : (
          <span className='text-gray-400'>No contact</span>
        )
    }
  ]

  if (hideWhenEmpty && !loading && rows.length === 0) {
    return null
  }

  // Two chips, per the owner: what came of the relationship, at a glance.
  // "Not invested" aggregates declined and not-invested, so it deliberately
  // counts MORE rows than carry that exact label — the tooltip says so, and
  // splits it, because otherwise the number and the rows disagree.
  const invested = rows.filter((r) => r.outcome === 'invested').length
  const declined = rows.filter((r) => r.outcome === 'declined').length
  const notInvestedOnly = rows.filter((r) => r.outcome === 'not_invested').length
  const notInvested = declined + notInvestedOnly

  return (
    <div data-component='LpFundraisingHistorySection'>
      <FormTable<Row>
        title='Fundraising History'
        dataSource={dataSource}
        columns={columns}
        includeActions={false}
        size='small'
        pagination={false}
        loading={loading}
        emptyText='We have never raised from this investor.'
        headerActions={
          <span className='flex items-center gap-2'>
            {rows.length > 0 ? (
              <>
                <span className='text-gray-400 text-[11.5px]'>
                  {rows.length} {rows.length === 1 ? 'process' : 'processes'}
                </span>
                {invested > 0 ? <Tag color='green'>Invested {invested}</Tag> : null}
                {notInvested > 0 ? (
                  <Tooltip
                    title={`${declined} declined, ${notInvestedOnly} not invested when the fund finished raising.`}
                  >
                    <Tag>Not invested {notInvested}</Tag>
                  </Tooltip>
                ) : null}
              </>
            ) : null}
            <Button
              size='small'
              type='primary'
              icon={<PlusOutlined />}
              disabled={!accountId || !quickAdd}
              onClick={() =>
                quickAdd?.openQuickAdd('prospect', {
                  // {value,label} — the drawer's investor field holds the pair
                  // (its valueTransformer unwraps it at save)
                  [ProspectFields.prospects__investor]: {
                    value: accountId,
                    label: accountName || 'Investor'
                  }
                })
              }
            >
              Prospect
            </Button>
          </span>
        }
      />
    </div>
  )
}
